import React, { Dispatch, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'typesafe-actions';
import TitlePage from '../../components/TitlePage';
import { ApplicationState } from '../../store';
import { loadRequest, sorterMovimentacoes } from '../../store/ducks/movimentacoes/actions';
import { maskCurrency, maskUnd, maskCapitalized } from '../../utils/mask';
import Filter from './components/Filter';
import TableSec from './components/TableSec';
import Modal from './components/Modal';
import { Container } from './styles';
import { Sorter, sorterParamsHandle } from '../../utils/sorte';
import TableSort from '../../components/TableSort';
import { useWindowSize } from '../../hook/useWindowSize';
import CardMobile from './components/CardMobile';
import GetStatus from '../../components/GetStatus';
import AnchorButton from '../../components/AnchorButton';
import { HandleClickRowActive } from '../../utils/handleclickrowactive';
import moment from 'moment';
import { Group } from './types';
import TextTable from '../../components/TextTable';
import Refresh from '../../components/Refresh';
import mask from '../../utils/mask';
import { MovimentacoesServices } from './services/movimentacoesService';
import { dadosFormatados } from './utils/formataTabela';
import { ExportBtn } from 'components/_REFACTORED/ExportBtn';
import { FilterOutlined } from '@ant-design/icons';
import { ContFilter } from 'modules/atendimento/style';

const Movimentacoes: React.FC = () => {
  const movimentacoes = useSelector((state: ApplicationState) => state.Movimentacoes.data);
  const loading = useSelector((state: ApplicationState) => state.Movimentacoes.loading);
  const pagination = useSelector((state: ApplicationState) => state.Movimentacoes.pagination);
  const inscricaoAtiva = useSelector((state: ApplicationState) => state.auth.inscricaoAtiva);
  const cddAtivo = useSelector((state: ApplicationState) => state.auth.cddAtivo);
  const filter = useSelector((state: ApplicationState) => state.Movimentacoes.filter);

  const [filterActive, setFilterActive] = useState<string[]>([]);

  useEffect(() => {
    if (filter)
      Object.keys(filter as {}).forEach((key) => {
        if (filter[key] === undefined || filter[key] === null || filter[key] === '') {
          delete filter[key];
        }
      });
    setFilterActive(Object.keys(filter ?? {}));
  }, [filter]);

  const dispatch = useDispatch<Dispatch<Action>>();
  const { width }: any = useWindowSize();

  const resultado = movimentacoes?.map((itemMovi) => ({
    ...itemMovi,
    pesoBruto: maskUnd(itemMovi.pesoBruto) + ' KG',
    tempoArmazenagemContratado: itemMovi.tempoArmazenagemContratado + ' Dias',
    tempoRestante: itemMovi.tempoRestante ? itemMovi.tempoRestante + ' Dias' : '',
    vlrMercadoria: maskCurrency(itemMovi.vlrMercadoria),
    dtEmissao: itemMovi.dtEmissao,
    dtLancamento: itemMovi.dtLancamento,
  }));

  const dataExport = dadosFormatados(movimentacoes);

  const getData = async () => {
    const { data } = await MovimentacoesServices.getNotasPrincipal(
      filter,
      filter?.codParceiro ? [+filter.codParceiro] : inscricaoAtiva,
      cddAtivo?.codCdd,
      pagination.total
    );

    return dadosFormatados(data.notas);
  };

  const columns: Group[] = [
    {
      title: 'N° único',
      dataIndex: 'nuNota',
      align: 'center',
      key: 'nuNota',
      sorter: {
        compare: Sorter.DEFAULT,
        multiple: 1,
      },
      render: (text: string) => {
        return text ? <TextTable>{text}</TextTable> : <TextTable>Não informado</TextTable>;
      },
    },
    {
      title: 'N° da nota',
      dataIndex: 'numNota',
      key: 'numNota',
      align: 'center',
      sorter: {
        compare: Sorter.DEFAULT,
        multiple: 2,
      },
      render: (text: string, record: any) => {
        return (
          <>
            <Modal text={text} nuNota={record?.nuNota} chave={record?.chaveNfe} />
          </>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 3,
      },
      render: (text: any, row: any) => {
        return <GetStatus acronym={text} />;
      },
    },
    {
      title: 'Data emissão',
      dataIndex: 'dtEmissao',
      key: 'dtEmissao',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 4,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{moment(text).format('LL')}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
    {
      title: 'Data lançamento',
      dataIndex: 'dtLancamento',
      key: 'dtLancamento',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 5,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{moment(text).format('LL')}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
    {
      title: 'Inscrição estadual',
      dataIndex: 'inscricaoEstadualParceiro',
      key: 'inscricaoEstadualParceiro',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 6,
      },
      render: (text: string) => {
        return text ? <TextTable>{text}</TextTable> : <TextTable>Não informado</TextTable>;
      },
    },

    {
      title: 'Cidade do depositante',
      dataIndex: 'cidadeUf',
      key: 'cidadeUf',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 7,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{mask('cidadeUf', text)}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
    {
      title: 'Depositante',
      dataIndex: 'parceiro',
      key: 'parceiro',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 7,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{mask('camelCase', text)}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
    {
      title: 'Emitente',
      dataIndex: 'emitente',
      key: 'emitente',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 8,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{mask('camelCase', text)}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
    {
      title: 'Pagador da armazenagem',
      dataIndex: 'pagadorArmazenagem',
      key: 'pagadorArmazenagem',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 9,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{mask('camelCase', text)}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
    {
      title: 'Tempo armazenagem contratado',
      dataIndex: 'tempoArmazenagemContratado',
      key: 'tempoArmazenagemContratado',
      align: 'center',
      sorter: {
        compare: Sorter.STRING,
        multiple: 10,
      },
      render: (text: string) => {
        return text !== '0 Dias' ? (
          <TextTable>{text}</TextTable>
        ) : (
          <TextTable>Não contratado</TextTable>
        );
      },
    },
    {
      title: 'Tempo restante',
      dataIndex: 'tempoRestante',
      key: 'tempoRestante',
      align: 'center',
      sorter: {
        compare: Sorter.DEFAULT,
        multiple: 11,
      },
      render: (text: string) => {
        return text ? <TextTable>{text}</TextTable> : <TextTable>Finalizado</TextTable>;
      },
    },
    {
      title: 'Peso bruto',
      dataIndex: 'pesoBruto',
      key: 'pesoBruto',
      align: 'center',
      sorter: {
        compare: Sorter.DEFAULT,
        multiple: 12,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{maskCapitalized(text)}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
    {
      title: 'Valor da mercadoria',
      dataIndex: 'vlrMercadoria',
      key: 'vlrMercadoria',
      align: 'center',
      sorter: {
        compare: Sorter.DEFAULT,
        multiple: 13,
      },
      render: (text: string) => {
        return text ? (
          <TextTable>{maskCapitalized(text)}</TextTable>
        ) : (
          <TextTable>Não informado</TextTable>
        );
      },
    },
  ];

  return (
    <>
      <Refresh
        loadRequest={() =>
          dispatch(loadRequest(filter, inscricaoAtiva, cddAtivo?.codCdd, pagination))
        }
      />
      <Filter />
      <Container>
        <TitlePage>
          <span>
            <AnchorButton textRoute={'movimentacoes'} />
            Movimentações
          </span>
          {filterActive.length > 0 && (
            <ContFilter>
              {filterActive.map((item) => (
                <p key={item}>
                  {item === 'produto' && (
                    <>
                      <FilterOutlined /> produto
                    </>
                  )}
                  {item === 'nuNota' && (
                    <>
                      <FilterOutlined />
                      Número único
                    </>
                  )}
                  {item === 'numNota' && (
                    <>
                      <FilterOutlined />
                      Número da nota
                    </>
                  )}
                  {item === 'status' && (
                    <>
                      <FilterOutlined />
                      Status{' '}
                    </>
                  )}
                  {item === 'pagadorArmazenagem' && (
                    <>
                      <FilterOutlined />
                      Pagador da armazenagem{' '}
                    </>
                  )}
                  {item === 'emissorNota' && (
                    <>
                      <FilterOutlined />
                      Emitente{' '}
                    </>
                  )}
                  {item === 'periodoEmissaoInicio' ||
                    (item === 'periodoEmissaoFim' && (
                      <>
                        <FilterOutlined />
                        Período emissão da nota{' '}
                      </>
                    ))}
                  {item === 'periodoLancamentoInicio' ||
                    (item === 'periodoLancamentoFim' && (
                      <>
                        <FilterOutlined />
                        Período lançamento{' '}
                      </>
                    ))}
                  {item === 'codDepositante' && (
                    <>
                      <FilterOutlined />
                      Depositante{' '}
                    </>
                  )}
                </p>
              ))}
            </ContFilter>
          )}
          <div className="box">
            <ExportBtn onGetAllData={getData} screenData={dataExport} filename={'movimentacoes'} />
          </div>
        </TitlePage>

        {width > 768 && (
          <TableSort
            style={{ fontSize: 10, display: 'inline' }}
            columns={columns}
            scroll={{ x: 2400 }}
            size={'large'}
            onRow={(record: any, rowIndex: any) => {
              return {
                onClick: (event: React.MouseEvent<HTMLElement>) =>
                  HandleClickRowActive(event, record),
              };
            }}
            onChange={(pagination: any, filters: any, sorter: any) => {
              dispatch(
                loadRequest(
                  filter,
                  filter?.codParceiro ? [+filter.codParceiro] : inscricaoAtiva,
                  cddAtivo?.codCdd,
                  pagination,
                  sorterParamsHandle(sorter)
                )
              );
              dispatch(sorterMovimentacoes(sorterParamsHandle(sorter)));
            }}
            pagination={pagination}
            className="table"
            dataSource={resultado}
            loading={loading}
            rowKey={'nuNota'}
            expandable={{
              expandedRowRender: (record: any) => <TableSec nuNota={record.nuNota} />,
            }}
          />
        )}
        {width <= 768 && (
          <>
            <CardMobile
              props={resultado}
              pagination={pagination}
              loading={loading}
              inscricaoAtiva={inscricaoAtiva}
              cddAtivo={cddAtivo}
              filter={filter}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default Movimentacoes;
